/**
 * UserNotesList Component
 *
 * Displays user's notes list with role-based tabs:
 * - Supervisors: My Notes | All Clinic Notes | Co-sign Queue (+ Edits Needed if count > 0)
 * - Admins (non-supervisor): All Clinic Notes | My Notes (+ Edits Needed if count > 0)
 * - Providers with edits requested: My Notes | Edits Needed
 * - Regular providers: plain notes list (no tabs)
 *
 * Remembers the last selected tab in localStorage.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Box, Tab, Tabs } from '@mui/material';

import { EntitlementUsage } from '@features/entitlements';
import { NotesListPage, NotesListContent, NoteQueueContent } from '@features/notes';
import { useNotificationSummaryPolling } from '@features/notifications';
import { useCan, useOrganizationId, useUserState } from '@features/user';

const NOTES_TAB_STORAGE_KEY = 'adminNotesTabPreference';

type TabValue = 'my-notes' | 'group' | 'cosign-queue' | 'edits-queue';

export const UserNotesList: React.FC = () => {
  // ADR 0073 Phase 3 (GDN-1590): catalog parity with hasAdminPrivileges
  // (manage_organization) and hasRole('supervisor') (sign_supervised_notes).
  const isAdmin = useCan('manage_organization');
  const isSupervisor = useCan('sign_supervised_notes');
  const userState = useUserState();
  const groupId = userState?.group_ids?.[0];
  const organizationId = useOrganizationId();

  const { cosignQueueCount, editsRequestedCount } = useNotificationSummaryPolling();

  // Determine which tabs to show
  const showTabs = isAdmin || isSupervisor || editsRequestedCount > 0;

  const tabs = useMemo(() => {
    const result: { value: TabValue; label: string; badge?: number }[] = [];

    if (isAdmin || isSupervisor) {
      result.push({ value: 'group', label: 'All Clinic Notes' });
    }

    result.push({ value: 'my-notes', label: 'My Notes' });

    if (isSupervisor) {
      result.push({
        value: 'cosign-queue',
        label: 'Co-sign Needed',
        badge: cosignQueueCount,
      });
    }

    if (editsRequestedCount > 0) {
      result.push({
        value: 'edits-queue',
        label: 'Edits Needed',
        badge: editsRequestedCount,
      });
    }

    return result;
  }, [isAdmin, isSupervisor, cosignQueueCount, editsRequestedCount]);

  const tabValues = useMemo(() => tabs.map(t => t.value), [tabs]);

  const getInitialTab = (): TabValue => {
    if (!showTabs) return 'my-notes';
    const saved = localStorage.getItem(NOTES_TAB_STORAGE_KEY) as TabValue | null;
    if (saved && tabValues.includes(saved)) return saved;
    return 'my-notes';
  };

  const [viewMode, setViewMode] = useState<TabValue>(getInitialTab);

  // Reset to valid tab if current tab is no longer available
  useEffect(() => {
    if (showTabs && !tabValues.includes(viewMode)) {
      setViewMode('my-notes');
    }
  }, [showTabs, tabValues, viewMode]);

  useEffect(() => {
    if (showTabs) {
      localStorage.setItem(NOTES_TAB_STORAGE_KEY, viewMode);
    }
  }, [viewMode, showTabs]);

  // No tabs needed — render simple view
  if (!showTabs) {
    return <NotesListPage title="Notes" />;
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Tabs
        value={viewMode}
        onChange={(_, newValue: TabValue) => setViewMode(newValue)}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 3,
        }}
      >
        {tabs.map(tab => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {tab.label}
                {tab.badge && tab.badge > 0 ? (
                  <Box
                    component="span"
                    sx={{
                      px: 0.75,
                      height: 18,
                      minWidth: 18,
                      borderRadius: '9px',
                      bgcolor: 'error.main',
                      color: 'white',
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {tab.badge > 99 ? '99+' : tab.badge}
                  </Box>
                ) : null}
              </Box>
            }
          />
        ))}
      </Tabs>

      {viewMode === 'group' && (
        <NotesListContent
          groupId={groupId}
          title="All Clinic Notes"
          showIssuingUser={true}
          enableDateFilter={false}
        />
      )}

      {viewMode === 'my-notes' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <EntitlementUsage capabilityName="note_creation" source="notes_list" />
          <div>
            <NotesListContent title="My Notes" />
          </div>
        </Box>
      )}

      {viewMode === 'cosign-queue' && (
        <NoteQueueContent
          title="Co-sign Needed"
          completionStatus="ready"
          organizationId={organizationId ?? undefined}
          cosignScope="mine"
          emptyMessage="No notes awaiting your co-signature."
        />
      )}

      {viewMode === 'edits-queue' && (
        <NoteQueueContent
          title="Edits Needed"
          completionStatus="edits_requested"
          emptyMessage="No notes require edits."
        />
      )}
    </Box>
  );
};
